import React, { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import * as R from 'ramda'

import moment from 'moment'

import { OrderCtx, Order, Address as ZupplerAddress, OrderAdjustment } from './model-order'
import {
  LensPath,
  LensPath2,
  CondLensPath,
  StackCtx,
  ConfigCtx,
  UserConfigCtx,
  LensPath3,
} from 'views/order/v5/comp-factory'
import { RestaurantCtx, Restaurant } from 'views/order/v5/model-restaurant'
import props from 'ramda/es/props'
import { SubjectRating, useFeedbackAPI } from './api/feedback'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { BlockedCustomer, useBlockCustomerAPI } from './api/block_customer_api'
import { useAuditResourceService, useAuditService } from 'api/audit'
import { EditOrderAction } from './edit-order/service'
import { Channel, ChannelCtx } from './model-channel'

const utils = require('views/order/components/utils')

const orderLens = (...path: string[]) => LensPath(R.lensPath(path), OrderCtx, path.join('-'))
const restaurantLens = (...path: string[]) => LensPath(R.lensPath(path), RestaurantCtx, path.join('-'))
const stackLens = (...path: string[]) => LensPath(R.lensPath(path), StackCtx, path.join('-'))
const propLens = (ctx: React.Context<any>, ...path: string[]) => LensPath(R.lensPath(path), ctx, path.join('-'))

const orderLens2 = (transformer: <T>(a: T) => string, ...path: string[]) =>
  LensPath(R.lensPath(path), OrderCtx, path.join('-'), transformer)

function orderRestaurantLens2<T>(transformer: (a: T, opt: Restaurant) => string, ...path: string[]) {
  return LensPath2(R.lensPath(path), OrderCtx, RestaurantCtx, path.join('-'), transformer)
}

function orderRestaurantChannelLens3<T>(
  transformer: (a: T, opt: Restaurant, opt2: Channel) => string,
  ...path: string[]
) {
  return LensPath3(R.lensPath(path), OrderCtx, RestaurantCtx, ChannelCtx, path.join('-'), transformer)
}

const restaurantLens2 = (transformer: <T>(a: T) => string, ...path: string[]) =>
  LensPath(R.lensPath(path), RestaurantCtx, path.join('-'), transformer)
const stackLens2 = (transformer: <T>(a: T) => string, ...path: string[]) =>
  LensPath(R.lensPath(path), StackCtx, path.join('-'), transformer)

const stackRestaurantLens2 = (transformer: (data: any, opt: Restaurant) => string, ...path: string[]) =>
  LensPath2(R.lensPath(path), StackCtx, RestaurantCtx, 'rest' + path.join('-'), transformer)

const condLensEquals = (value: any, ...path: string[]) =>
  CondLensPath(R.equals(value), R.lensPath(path), OrderCtx, path.join('-'))

const condStackLensEquals = (value: any, ...path: string[]) =>
  CondLensPath(R.equals(value), R.lensPath(path), StackCtx, path.join('-'))
const condStackLensNotEquals = (value: any, ...path: string[]) =>
  CondLensPath(R.compose(R.not, R.equals(value)), R.lensPath(path), StackCtx, path.join('-'))
const condOrderLensEquals = (value: any, ...path: string[]) =>
  CondLensPath(R.equals(value), R.lensPath(path), OrderCtx, path.join('-'))
const condOrderLensNotEquals = (value: any, ...path: string[]) =>
  CondLensPath(R.compose(R.not, R.equals(value)), R.lensPath(path), OrderCtx, path.join('-'))

const toCurrency = (val: number, restaurant: Restaurant) => utils.currency(restaurant.locale, val)
const toCurrencyDollars = (val: number, restaurant: Restaurant) => utils.currency(restaurant.locale, val * 100)
const toJSON = (val: string) => JSON.parse(val)
const formatDateTime = (val: string) => moment(val).format('LLLZ')

const toOptCurrency = (val: number, restaurant: Restaurant) => (val != 0 ? utils.currency(restaurant.locale, val) : '')

const makeWord = (str: string) => str.slice(0, 1).toLocaleUpperCase() + str.slice(1, str.length).replace('_', ' ')

const extractPath = (ctx: React.Context<unknown>, ...path: string[]) => (
  props: PropsWithChildren<unknown>
): JSX.Element => {
  const data = useContext(ctx)
  if (data) {
    return <StackCtx.Provider value={R.path(path, data)}>{props.children}</StackCtx.Provider>
  } else {
    return <></>
  }
}

function iteratePath<T>(displayName: string, Ctx: React.Context<any>, lens: R.Lens, transformer: (a: T) => any) {
  const fn = (props: { children: any }) => {
    const data = useContext(Ctx)
    if (data) {
      const iterable = R.view(lens, data) as any[]
      if (iterable && iterable.length) {
        return (
          <>
            {iterable.map((it, index) => (
              <StackCtx.Provider key={index} value={transformer(it)}>
                {props.children}
              </StackCtx.Provider>
            ))}
          </>
        )
      }
    }
    return null
  }
  fn.displayName = displayName
  return fn
}

function iteratePath2<T>(
  displayName: string,
  Ctx: React.Context<any>,
  Ctx2: React.Context<any>,
  lens: R.Lens,
  transformer: (a: T) => any
) {
  const fn = (props: { children: any }) => {
    const data = useContext(Ctx)
    if (data) {
      const iterable = R.view(lens, data) as any[]
      if (iterable && iterable.length) {
        return (
          <>
            {iterable.map((it, index) => (
              <StackCtx.Provider key={index} value={transformer(it)}>
                <Ctx2.Provider key={index} value={transformer(it)}>
                  {props.children}
                </Ctx2.Provider>
              </StackCtx.Provider>
            ))}
          </>
        )
      }
    }
    return null
  }
  fn.displayName = displayName
  return fn
}

export const OrderUUID = orderLens('uuid')
export const ShortOrderUUID = orderLens2(
  // no R.toString here - it would wrap the value in literal quotes
  R.compose(R.defaultTo(''), R.head, R.split('-')),
  'uuid'
)

export const CustomerName = orderLens('carts', 'customer', 'name')
export const CustomerPhone = orderLens('carts', 'customer', 'phone')
export const CustomerEmail = orderLens('carts', 'customer', 'email')

export const BlockButton = (
  props: { type: 'address'; address: ZupplerAddress } | { type: 'email' | 'phone'; value: string }
): JSX.Element => {
  const blockCustomerAPI = useBlockCustomerAPI()

  const [isOpen, setIsOpen] = useState(false)
  const [isBlocked, setIsBlocked] = useState(false)

  const auditResourceAPI = useAuditResourceService()
  const auditAPI = useAuditService()

  useEffect(() => {
    if (blockCustomerAPI.apiAvailable) {
      if (props.type == 'address' && props.address) {
        const addressObj = { value: props.address.full, lat: props.address.lat, lng: props.address.lng }
        blockCustomerAPI.checkBlockedCustomer(props.type, addressObj).then((response) => {
          if (response.blocked) {
            setIsBlocked(true)
          }
        })
      } else if (props.type == 'phone' || props.type == 'email') {
        blockCustomerAPI.checkBlockedCustomer(props.type, props.value).then((response) => {
          if (response.blocked) {
            setIsBlocked(true)
          }
        })
      }
    }
  }, [props.type])

  const onSave = (params: Partial<BlockedCustomer>): void => {
    // TODO: Frank, in case of server side errors the system should reply with 4xx error. This should be handled on a
    // catch clause and not with then
    blockCustomerAPI
      .createBlockedCustomer(params)
      .then((response) => {
        if (response.errors) {
        } else {
          setIsBlocked(true)
          setIsOpen(false)

          auditAPI.createCrossLog(
            'ControlPanel',
            'BlockCustomer',
            'BlockCustomer',
            'BlockCustomer',
            `Blocked ${props.type} of ${props.type === 'address' ? props.address.full : (props.value as string)}`
          )
          if (auditResourceAPI) {
            auditResourceAPI.createLog(
              'BlockCustomer',
              `Blocked ${props.type} of ${props.type === 'address' ? props.address.full : (props.value as string)}`
            )
          }
        }
      })
      .catch((response: { errors: { [k: string]: string[] } }) => {
        const errors = Object.keys(response.errors)
          .map((key) => {
            return `${props.type} ${response.errors[key].join('. ')}`
          })
          .join('\n')
        alert(errors)
      })
  }

  return blockCustomerAPI.apiAvailable ? (
    <>
      {isOpen &&
        (props.type === 'address' ? (
          <BlockModal type={props.type} address={props.address} onClose={() => setIsOpen(false)} onSave={onSave} />
        ) : (
          <BlockModal type={props.type} value={props.value} onClose={() => setIsOpen(false)} onSave={onSave} />
        ))}
      <span className="text-success" style={{ backgroundColor: 'whitesmoke', padding: '1px' }}>
        <i
          style={{ cursor: 'pointer' }}
          className={isBlocked ? 'fa fa-ban' : 'fa fa-check'}
          onClick={() => (!isBlocked ? setIsOpen(true) : 0)}
        />
      </span>
    </>
  ) : (
    <></>
  )
}

const BlockModal = (
  props: { onClose: () => void; onSave: (blockedCustomer: Partial<BlockedCustomer>) => void } & (
    | { type: 'address'; address: ZupplerAddress }
    | { type: 'email' | 'phone'; value: string }
  )
): JSX.Element => {
  const message =
    props.type == 'address' && props.address
      ? `${props.address.full}, (${props.address.lat},${props.address.lng})`
      : props.type == 'email' || props.type == 'phone'
      ? (props.value as string)
      : 'unkown'

  const blockedCustomer: Partial<BlockedCustomer> = {
    type: props.type,
    ...(props.type === 'address' && props.address
      ? { value: props.address.full, lat: props.address.lat, lng: props.address.lng }
      : props.type !== 'address'
      ? { value: props.value }
      : {}),
  }

  return (
    <div>
      <Modal isOpen={true} toggle={props.onClose}>
        <ModalHeader toggle={props.onClose}>
          <span>Block customer {props.type}</span>
        </ModalHeader>
        <ModalBody>
          Are you sure you want to block customer {props.type} {message}?
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => props.onSave(blockedCustomer)}>
            Save
          </Button>
          <Button color="secondary" onClick={props.onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export const CustomerRegistered = orderLens2(
  (id: any) => {
    return id ? (
      <span className="customer-chip" title="Registered customer">
        Registered
      </span>
    ) : (
      <span className="customer-chip guest" title="Guest customer">
        Guest
      </span>
    )
  },
  'carts',
  'customer',
  'user_id'
)

export const State = orderLens('state')
export const WorkflowState = orderLens('workflow_node_id')

export const StateContainer = (props: { children: any }) => {
  const order = useContext(OrderCtx)
  if (order) return <div className={`order-${order.state}`}>{props.children}</div>
  return null
}

export const ItemsCtx = React.createContext<any | null>(null)
export const ModifiersCtx = React.createContext<any | null>(null)
export const OptionsCtx = React.createContext<any | null>(null)

export const Items = iteratePath2('Items', OrderCtx, ItemsCtx, R.lensPath(['carts', 'items']), R.identity)

export const Item = {
  Quantity: () => {
    const config = useContext(UserConfigCtx)
    const stack = useContext(StackCtx)
    if ((config && config.getConfig('items.min.quantity')) || stack.quantity > 1) {
      return <>{stack.quantity} x</>
    } else {
      return null
    }
  },
  WithDeleted: (props: ReactWithChildren<{}>): JSX.Element => {
    const currentItem = useContext(ItemsCtx)
    return (
      <span
        style={{
          textDecoration:
            currentItem.adjustments && currentItem.adjustments.find((x) => x.price == -currentItem.price)
              ? 'line-through'
              : 'none',
        }}>
        {props.children}
      </span>
    )
  },
  Total: stackRestaurantLens2(toOptCurrency, 'item_total'),
  TotalByQuantity: () => {
    const restaurantCtx = useContext(RestaurantCtx)
    const stackCtx = useContext(StackCtx)
    const config = useContext(UserConfigCtx)
    return (
      <>
        {toOptCurrency(
          (config && config.getConfig('item.show_full_price') ? stackCtx.quantity : 1) * stackCtx.item_total,
          restaurantCtx as Restaurant
        )}
      </>
    )
  },
  Modifiers: iteratePath2('Modifiers', StackCtx, ModifiersCtx, R.lensPath(['modifiers']), R.identity),
  Options: iteratePath2('Options', StackCtx, OptionsCtx, R.lensPath(['options']), R.identity),
  FullName: () => {
    const config = useContext(UserConfigCtx)
    if (config)
      return (
        <>
          {config.getConfig('items.menu.name') && (
            <>
              <Data.Menu /> /{' '}
            </>
          )}
          {config.getConfig('items.menu.category') && (
            <>
              <Data.Category /> /{' '}
            </>
          )}
          <Data.Name />
        </>
      )
    else return <Data.Name />
  },
  Rating: () => {
    const feedbackAPI = useFeedbackAPI()
    const stack = useContext(StackCtx)

    const itemRating = feedbackAPI.getItemRatings().find((ir: SubjectRating) => ir.name == stack.name)
    console.log('Stack', stack, itemRating)
    return <>{itemRating != null}</>
  },
}

export const Data = {
  Name: stackLens('name'),
  Group: stackLens('group'),
  Value: stackLens('value'),
  Menu: stackLens('menu'),
  Category: stackLens('category'),
  DishName: stackLens('dish_name'),
  Size: stackLens('size_name'),
  Price: stackRestaurantLens2(toOptCurrency, 'price'),
  Total: stackRestaurantLens2(toOptCurrency, 'total'),
  Quantity: stackLens('quantity'),
  ItemTotal: stackRestaurantLens2(toOptCurrency, 'item_total'),
  Comments: stackLens('comments'),
}

export const ConfCode = orderLens('ivr_code')

function substractMarkup(value: number, order: Order, restaurant: Restaurant, channel: Channel): string {
  return toCurrency(channel.menuMarkupHidden ? value - order.totals.total_markup : value, restaurant)
}

function addHiddenMarkup(value: number, order: Order, restaurant: Restaurant, channel: Channel): string {
  return toCurrency(channel.menuMarkupHidden ? value + order.totals.total_markup : value, restaurant)
}

const TotalFields = ['subtotal', 'discount', 'delivery', 'service', 'tax', 'tip', 'tender_discount']

interface TotalProps {
  hideZero?: boolean
  children?: any
}

export const Totals = {
  Iterate: iteratePath(
    'Totals',
    OrderCtx,
    R.lens(
      (data: Order) => TotalFields.map((field) => ({ id: field, label: field, value: data.totals[field] })),
      R.identity
    ),
    R.identity
  ),
  IsZero: condStackLensEquals(0, 'value'),
  IsntZero: condStackLensNotEquals(0, 'value'),
  IsntZeroOrRequired: condStackLensNotEquals(0, 'value'),
  IsDelivery: condStackLensEquals('delivery', 'id'),
  IsTip: condStackLensEquals('tip', 'id'),
  Label: stackLens2(makeWord, 'label'),
  Value: stackRestaurantLens2(toCurrency, 'value'),
  GrandTotal: orderRestaurantLens2(toCurrency, 'totals', 'total'),
  ConvenienceFee: {
    Label: stackLens('label'),
    Value: orderRestaurantLens2(toCurrency, 'totals', 'hidden'),
  },
}

export const Totals2 = {
  Subtotal: orderRestaurantLens2(toCurrency, 'totals', 'subtotal'),
  Discount: orderRestaurantLens2(toCurrency, 'totals', 'discount'),
  Delivery: orderRestaurantLens2(toCurrency, 'totals', 'delivery'),
  Service: orderRestaurantChannelLens3(substractMarkup, 'totals', 'service'),
  Tax: orderRestaurantLens2(toCurrency, 'totals', 'tax'),
  Tip: orderRestaurantLens2(toCurrency, 'totals', 'tip'),
  Total: orderRestaurantChannelLens3(substractMarkup, 'totals', 'total'),
  ConvenienceFee: orderRestaurantChannelLens3(addHiddenMarkup, 'totals', 'hidden'),
}

export const AdjustmentsCtx = createContext(null)
export const AdjustmentActionsCtx = createContext(null)
export const AdjustmentsTotalDeltaCtx = createContext<{ name: string; value: number } | null>(null)

function AdjustmentsTotalDeltas(props: PropsWithChildren<{}>): JSX.Element {
  const adjustment = (useContext(AdjustmentsCtx) as unknown) as OrderAdjustment
  const { subtotal, delivery_charge, discount, service_charge, tax, tip, total } = adjustment
  const data = [
    { name: 'Subtotal', value: subtotal },
    { name: 'Delivery Charge', value: delivery_charge },
    { name: 'Discount', value: discount },
    { name: 'Service Charge', value: service_charge },
    { name: 'Tax', value: tax },
    { name: 'Tip', value: tip },
    { name: 'Total', value: total },
  ]
  return (
    <>
      {data
        .filter(({ value }) => value != 0)
        .map((it, index) => (
          <AdjustmentsTotalDeltaCtx.Provider key={index} value={it}>
            {props.children}
          </AdjustmentsTotalDeltaCtx.Provider>
        ))}
    </>
  )
}

function AdjustmentTotal(): JSX.Element {
  const data = useContext(AdjustmentsTotalDeltaCtx)
  const restaurant = useContext(RestaurantCtx)
  console.log('Data', data)
  if (!data) return <></>
  return (
    <>
      {data.name}: {toCurrency(data.value, restaurant)}
    </>
  )
}

export const Adjustments = {
  Iterate: iteratePath2('Adjustments', OrderCtx, AdjustmentsCtx, R.lensPath(['adjustments']), R.identity),
  User: {
    Name: stackLens('user_name'),
    Email: stackLens('user_email'),
  },
  State: stackLens('state'),
  Comments: stackLens('comments'),
  Actions: {
    Iterate: iteratePath2(
      'Adjustments',
      AdjustmentsCtx,
      AdjustmentActionsCtx,
      R.lens(
        (data) => JSON.parse(data.actions),
        (x, data) => data
      ),
      R.identity
    ),
    Description: stackLens('description'),
  },
  Totals: {
    Iterate: AdjustmentsTotalDeltas,
    // Name: propLens(AdjustmentsTotalDeltaCtx, 'name'),
    // Value: propLens(AdjustmentsTotalDeltaCtx, 'value'),
    Total: AdjustmentTotal,
  },
  CreatedAt: stackLens2(formatDateTime, 'inserted_at'),
}

export const ChargesCtx = React.createContext(null)

export const Charges = {
  Iterate: iteratePath2('Charges', OrderCtx, ChargesCtx, R.lensPath(['carts', 'charges']), R.identity),
  Id: stackLens('id'),
  Name: stackLens('name'),
  Amount: stackRestaurantLens2(toCurrencyDollars, 'amount'),
}

export const CartChargesCtx = React.createContext(null)

export const CartCharges = {
  Iterate: iteratePath2('CartCharges', OrderCtx, CartChargesCtx, R.lensPath(['carts', 'cartCharges']), R.identity),
  Id: stackLens('id'),
  Name: stackLens('name'),
  Amount: stackRestaurantLens2(toCurrencyDollars, 'amount'),
  Taxable: stackRestaurantLens2(toCurrencyDollars, 'taxable'),
}

function fromCents(cents: number): number {
  return cents / 100
}

export const NamedTotals = {
  Subtotal: orderRestaurantLens2(toCurrency, 'totals', 'subtotal'),
  Discount: orderRestaurantLens2(toCurrency, 'totals', 'discount'),
  HasDiscount: condOrderLensNotEquals(0, 'totals', 'discount'),
  HasNoDiscount: condOrderLensEquals(0, 'totals', 'discount'),
  Delivery: orderRestaurantLens2(toCurrency, 'totals', 'delivery'),
  Service: orderRestaurantLens2(toCurrency, 'totals', 'service'),
  Tax: orderRestaurantLens2(toCurrency, 'totals', 'tax'),
  Tip: orderRestaurantLens2(toCurrency, 'totals', 'tip'),
  Total: orderRestaurantLens2(toCurrency, 'totals', 'total'),
}

export const RestaurantName = restaurantLens('name')
export const RestaurantLocale = restaurantLens('locale')

export const Service = {
  Label: orderLens('carts', 'settings', 'service', 'label'),
  IsDelivery: condLensEquals('DELIVERY', 'carts', 'settings', 'service', 'id'),
  IsCurbside: condLensEquals('CURBSIDE', 'carts', 'settings', 'service', 'id'),
  IsPickup: condLensEquals('PICKUP', 'carts', 'settings', 'service', 'id'),
  DeliveryAddress: extractPath(OrderCtx, 'carts', 'settings', 'service', 'address'),
  RestaurantAddress: extractPath(OrderCtx, 'carts', 'settings', 'restaurant_location_address'),
  CurbsideInfo: orderLens('carts', 'settings', 'curbside_info'),
}

export const Address = {
  Street: stackLens('street'),
  Block: stackLens('block'),
  City: stackLens('city'),
  State: stackLens('state'),
  Zip: stackLens('zip'),
  Country: stackLens('country'),
  Cross_street: stackLens('cross_street'),
  Full: stackLens('full'),
  Instructions: stackLens('delivery_instructions'),
}

export const IsAsap = condLensEquals('ASAP', 'carts', 'settings', 'time', 'id')
export const IsLater = condLensEquals('SCHEDULED', 'carts', 'settings', 'time', 'id')
export const IsDelivery = condLensEquals('DELIVERY', 'carts', 'settings', 'service', 'id')
export const OrderTime = orderLens2((t) => moment(t).format('LLLZ'), 'carts', 'settings', 'time', 'value')
export const OrderTimeOffset = orderLens2((t) => moment(t).fromNow(), 'carts', 'settings', 'time', 'value')
export const PickupTime = orderLens2((t) => moment(t).format('LLLZ'), 'pickup_time')
export const Tender = orderLens('carts', 'settings', 'tender', 'label')

export const TipValue = orderRestaurantLens2<number>(toCurrency, 'carts', 'settings', 'tip', 'value')

export const ExtraFields = (props: { children: any }) => {
  const order = useContext(OrderCtx)
  if (order && order.carts.extra_fields) {
    const extraFields = R.sortBy(R.prop('group'), R.values(JSON.parse(order.carts.extra_fields)))
    return (
      <>
        {extraFields.map((field: { group: string; name: string; value: string }, index: number) => (
          <StackCtx.Provider key={index} value={field}>
            {props.children}
          </StackCtx.Provider>
        ))}
      </>
    )
  }
  return null
}

export const ItemComments = (props: { children: any }) => {
  const stack = useContext(StackCtx)
  if (stack.comments) {
    return <>{props.children}</>
  } else {
    return <></>
  }
}

export const ContextItemName = () => {
  const stack = useContext(StackCtx)
  return stack?.ci_name ? <> ({stack.ci_name})</> : <></>
}